/**
 * A4 (vague « Fluidité ») — Plan annoté : pointer sur le plan, voir les actions, exporter.
 *
 * Même composant au bureau et sur le terrain : le corps ci-dessous est copié à
 * l'identique depuis terrain/src/components/plans/PlanAnnote.jsx (seul
 * l'import de l'API diffère ; contrôle : backend/tests/isolated/a4_plans_annotes.py).
 *
 * - Rendu de la page côté serveur (GET /plans-annotes/rendu, PNG) ; zoom au doigt
 *   (pincement), à la molette, boutons + / − ; déplacement à un doigt.
 * - Toucher un endroit → Réserve / Action / Photo (formulaire court) : l'action porte sa
 *   position (x, y relatifs, page) rattachée au PLAN et non à la version : elle est
 *   reportée sur le nouvel indice.
 * - Pastilles colorées par statut (À faire / À vérifier / Levée), filtres statut /
 *   entreprise / lot ; toucher une pastille → la fiche de l'action.
 * - « Placer sur le plan » une action existante (prop `placement`).
 * - Annoter : trait, flèche, texte, zone (observation de visite).
 * - Export PDF du plan annoté (épingles numérotées + liste des actions).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import api from './planApi'; // bureau : adaptateur (même interface que le client du terrain)

// ---- corps partagé ----
const C = {
  fond: '#1E1E1E', barre: '#2C2C2C', accent: '#C4773B', texte: '#2C2C2C', gris: '#6B6B6B',
  bord: '#E0E0DB', clair: '#FAFAF8',
};
const TYPES_CREATION = [
  { cle: 'reserve', libelle: 'Réserve' },
  { cle: 'action', libelle: 'Action' },
  { cle: 'photo', libelle: 'Photo' },
];
const OUTILS = [
  { cle: 'trait', libelle: 'Trait' },
  { cle: 'fleche', libelle: 'Flèche' },
  { cle: 'texte', libelle: 'Texte' },
  { cle: 'zone', libelle: 'Zone' },
];
const COULEURS_TRAIT = ['#C0392B', '#1E3A5F', '#2D6A4F'];
const PW = 1000; // largeur du plan en unités internes (indépendante de la résolution de l'image)
const enc = encodeURIComponent;

const btn = (actif, extra = {}) => ({
  minHeight: 44, minWidth: 44, padding: '0 12px', border: `1px solid ${actif ? C.accent : 'rgba(255,255,255,0.25)'}`,
  background: actif ? C.accent : 'transparent', color: 'white', fontSize: 13, fontWeight: 600,
  fontFamily: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  justifyContent: 'center', whiteSpace: 'nowrap', ...extra,
});
const champ = {
  width: '100%', padding: '10px 12px', fontSize: 15, border: `1px solid ${C.bord}`, borderRadius: 6,
  fontFamily: 'inherit', boxSizing: 'border-box', background: 'white', color: C.texte,
};
const etiquette = { display: 'block', fontSize: 13, fontWeight: 600, color: C.texte, margin: '0 0 6px' };
const dateFr = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const libelleIndice = (v) => (v?.indice ? `Indice ${v.indice}` : 'Sans indice');

// Rendu dans <body> : une carte qui glisse (transform) ne doit pas piéger l'écran plein.
const auCorps = (el) => (typeof document !== 'undefined' ? createPortal(el, document.body) : el);

export function Feuille({ titre, onFermer, children, pied, testid }) {
  return auCorps(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      data-testid={testid}>
      <div onClick={onFermer} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <div style={{ position: 'relative', background: 'white', borderRadius: '14px 14px 0 0', maxHeight: '88vh', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.bord}` }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.texte }}>{titre}</h2>
          <button onClick={onFermer} aria-label="Fermer" style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, color: C.gris, minWidth: 44, minHeight: 44, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', padding: 18, flex: 1 }}>{children}</div>
        {pied && (
          <div style={{ display: 'flex', gap: 10, padding: '12px 18px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${C.bord}` }}>
            {pied}
          </div>
        )}
      </div>
    </div>,
  );
}

function FormulaireAction({ plan, point, lots, onAnnuler, onCree, chantierId, typeInitial }) {
  const lotDuPlan = lots.find((l) => l.id === plan.lot_id || (plan.lot_code && l.code === plan.lot_code));
  const [type, setType] = useState(typeInitial || 'reserve');
  const [texte, setTexte] = useState('');
  const [photo, setPhoto] = useState(null);
  const [lot, setLot] = useState(lotDuPlan?.code || '');
  const [entreprise, setEntreprise] = useState(lotDuPlan?.entreprise_nom || '');
  const [echeance, setEcheance] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const apercu = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (apercu) URL.revokeObjectURL(apercu); }, [apercu]);
  const entreprises = useMemo(() => [...new Set(lots.map((l) => (l.entreprise_nom || '').trim()).filter(Boolean))].sort(), [lots]);

  const choisirLot = (code) => {
    setLot(code);
    const l = lots.find((x) => x.code === code);
    if (l?.entreprise_nom) setEntreprise(l.entreprise_nom);
  };
  const valide = type === 'photo' ? (!!photo || texte.trim().length >= 3) : texte.trim().length >= 3;

  const enregistrer = async () => {
    if (!valide || envoi) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setErreur("Pas de réseau : l'action n'est pas encore enregistrée. Gardez cet écran ouvert et réessayez dès que le réseau revient.");
      return;
    }
    setEnvoi(true);
    setErreur(null);
    const fd = new FormData();
    fd.append('plan_key', plan.cle);
    fd.append('document_id', plan.version?.document_id || '');
    fd.append('page', String(point.page));
    fd.append('x', String(point.x));
    fd.append('y', String(point.y));
    fd.append('type', type);
    fd.append('titre', texte.trim().split('\n')[0].slice(0, 200));
    fd.append('description', texte.trim());
    fd.append('lot', lot);
    fd.append('entreprise', entreprise);
    fd.append('echeance', echeance);
    if (photo) fd.append('photo', photo, photo.name || `photo_${Date.now()}.jpg`);
    try {
      const cree = await api.post(`/chantiers/${chantierId}/plans-annotes/actions`, fd);
      toast.success(type === 'reserve' ? 'Réserve enregistrée sur le plan' : type === 'photo' ? 'Photo enregistrée sur le plan' : 'Action enregistrée sur le plan');
      onCree(cree);
    } catch (e) {
      setErreur(e.message || "L'enregistrement a échoué. Réessayez.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Feuille
      titre="Nouveau sur le plan"
      onFermer={onAnnuler}
      testid="plan-annote-formulaire"
      pied={(
        <>
          <button onClick={onAnnuler} style={{ ...champ, flex: 1, fontWeight: 600, color: C.gris, cursor: 'pointer' }}>Annuler</button>
          <button onClick={enregistrer} disabled={!valide || envoi}
            style={{ ...champ, flex: 2, fontWeight: 700, color: 'white', border: 'none', background: valide && !envoi ? C.texte : '#9A9A9A', cursor: valide ? 'pointer' : 'not-allowed' }}
            data-testid="plan-annote-enregistrer">
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      )}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }} role="radiogroup" aria-label="Type">
        {TYPES_CREATION.map((t) => (
          <button key={t.cle} onClick={() => setType(t.cle)} role="radio" aria-checked={type === t.cle}
            style={{ flex: 1, minHeight: 44, border: `1px solid ${type === t.cle ? C.texte : C.bord}`, background: type === t.cle ? C.texte : 'white', color: type === t.cle ? 'white' : C.gris, fontSize: 14, fontWeight: 700, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}
            data-testid={`plan-annote-type-${t.cle}`}>
            {t.libelle}
          </button>
        ))}
      </div>
      <label style={etiquette} htmlFor="pa-photo">Photo</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <label style={{ ...champ, width: 'auto', display: 'inline-flex', alignItems: 'center', minHeight: 44, cursor: 'pointer', fontWeight: 600 }}>
          {photo ? 'Changer la photo' : 'Prendre une photo'}
          <input id="pa-photo" type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="plan-annote-photo" />
        </label>
        {apercu && <img src={apercu} alt="" style={{ width: 64, height: 64, objectFit: 'cover', border: `1px solid ${C.bord}` }} />}
      </div>
      <label style={etiquette} htmlFor="pa-texte">{type === 'photo' ? 'Commentaire' : 'Que faut-il faire ?'}{type !== 'photo' && <span style={{ color: '#C0392B' }}> *</span>}</label>
      <textarea id="pa-texte" value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} maxLength={1000}
        placeholder={type === 'reserve' ? 'Ex. : fissure en pied de voile, à reprendre' : 'Ex. : reprendre la réservation'}
        style={{ ...champ, resize: 'vertical', marginBottom: 16 }} data-testid="plan-annote-texte" />
      {lots.length > 0 && (
        <>
          <label style={etiquette} htmlFor="pa-lot">Lot</label>
          <select id="pa-lot" value={lot} onChange={(e) => choisirLot(e.target.value)} style={{ ...champ, marginBottom: 16 }} data-testid="plan-annote-lot">
            <option value="">— Aucun lot —</option>
            {lots.map((l) => <option key={l.id} value={l.code}>{[l.code, l.nom].filter(Boolean).join(' · ')}</option>)}
          </select>
        </>
      )}
      <label style={etiquette} htmlFor="pa-ent">Entreprise</label>
      <select id="pa-ent" value={entreprise} onChange={(e) => setEntreprise(e.target.value)} style={{ ...champ, marginBottom: 16 }} data-testid="plan-annote-entreprise">
        <option value="">— Aucune —</option>
        {entreprise && !entreprises.includes(entreprise) && <option value={entreprise}>{entreprise}</option>}
        {entreprises.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <label style={etiquette} htmlFor="pa-ech">Échéance</label>
      <input id="pa-ech" type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} style={champ} data-testid="plan-annote-echeance" />
      {erreur && <p style={{ margin: '14px 0 0', padding: 10, background: '#FDECEA', color: '#8B3A3A', fontSize: 13, borderRadius: 6 }} data-testid="plan-annote-erreur">{erreur}</p>}
    </Feuille>
  );
}

function FicheEpingle({ pin, onFermer, onOuvrirAction, onRetirer, peutAnnoter }) {
  return (
    <Feuille titre={`N° ${pin.numero} — ${pin.statut_libelle}`} onFermer={onFermer} testid="plan-annote-fiche"
      pied={(
        <>
          {peutAnnoter && (
            <button onClick={() => onRetirer(pin)} style={{ ...champ, flex: 1, color: '#8B3A3A', fontWeight: 600, cursor: 'pointer' }}>Retirer du plan</button>
          )}
          {onOuvrirAction && (
            <button onClick={() => onOuvrirAction(pin.id)} style={{ ...champ, flex: 2, border: 'none', background: C.texte, color: 'white', fontWeight: 700, cursor: 'pointer' }}
              data-testid="plan-annote-ouvrir-fiche">
              Ouvrir la fiche de l'action
            </button>
          )}
        </>
      )}>
      <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: C.texte }}>{pin.titre}</p>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: C.gris }}>
        <span style={{ display: 'inline-block', padding: '2px 8px', background: pin.couleur, color: 'white', fontWeight: 700, marginRight: 8 }}>{pin.statut_libelle}</span>
        {[pin.entreprise, pin.lot, pin.echeance ? `Échéance ${dateFr(pin.echeance)}` : ''].filter(Boolean).join(' · ')}
      </p>
      {pin.proposition && <p style={{ margin: '0 0 12px', fontSize: 12, color: C.gris }}>Proposition à valider : l'entreprise ne la voit pas encore.</p>}
      {pin.indice_pose && <p style={{ margin: '0 0 12px', fontSize: 12, color: C.gris }}>Placée sur l'indice {pin.indice_pose}.</p>}
      {pin.media_url && <PhotoAuthentifiee url={pin.media_url} />}
    </Feuille>
  );
}

function PhotoAuthentifiee({ url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let vivant = true;
    let objet = null;
    api.getBlob(String(url).replace(/^\/api/, '')).then((b) => {
      objet = URL.createObjectURL(b);
      if (vivant) setSrc(objet);
    }).catch(() => {});
    return () => { vivant = false; if (objet) URL.revokeObjectURL(objet); };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />;
}

function SaisieTexte({ onValider, onAnnuler }) {
  const [t, setT] = useState('');
  return (
    <Feuille titre="Texte sur le plan" onFermer={onAnnuler} testid="plan-annote-saisie-texte"
      pied={(
        <>
          <button onClick={onAnnuler} style={{ ...champ, flex: 1, fontWeight: 600, color: C.gris, cursor: 'pointer' }}>Annuler</button>
          <button onClick={() => t.trim() && onValider(t.trim())} disabled={!t.trim()}
            style={{ ...champ, flex: 2, border: 'none', background: t.trim() ? C.texte : '#9A9A9A', color: 'white', fontWeight: 700, cursor: 'pointer' }}>Ajouter</button>
        </>
      )}>
      <input autoFocus value={t} maxLength={300} onChange={(e) => setT(e.target.value)} style={champ} placeholder="Ex. : voir détail 3" />
    </Feuille>
  );
}

export default function PlanAnnote({ chantierId, cle, documentId, onFermer, onOuvrirAction, placement, onPlace, focusPin }) {
  const [plan, setPlan] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [page, setPage] = useState(1);
  const [versionId, setVersionId] = useState(documentId || null);
  const [image, setImage] = useState(null); // { url, w, h, largeur }
  const [vue, setVue] = useState({ s: 1, tx: 0, ty: 0 });
  const [taille, setTaille] = useState({ w: 0, h: 0 });
  const [mode, setMode] = useState(placement ? 'placer' : 'consulter');
  const [outil, setOutil] = useState('trait');
  const [couleur, setCouleur] = useState(COULEURS_TRAIT[0]);
  const [trace, setTrace] = useState(null);
  const [point, setPoint] = useState(null); // point touché (création / placement)
  const [choix, setChoix] = useState(false); // menu Réserve / Action / Photo
  const [formType, setFormType] = useState(null);
  const [texteEnAttente, setTexteEnAttente] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [filtres, setFiltres] = useState({ statuts: [], entreprise: '', lot: '' });
  const [panneauFiltres, setPanneauFiltres] = useState(false);
  const [lots, setLots] = useState([]);
  const [export_, setExport] = useState(false);
  const cadre = useRef(null);
  const pointeurs = useRef(new Map());
  const geste = useRef(null);
  const vueRef = useRef(vue);
  vueRef.current = vue;
  const PH = image ? (PW * image.h) / image.w : PW * 0.7;

  const charger = useCallback(async () => {
    try {
      const q = `cle=${enc(cle)}${versionId ? `&document_id=${enc(versionId)}` : ''}`;
      const d = await api.get(`/chantiers/${chantierId}/plans-annotes/plan?${q}`);
      setPlan(d);
      setErreur(null);
      return d;
    } catch (e) {
      setErreur(e.message || "Le plan n'a pas pu être ouvert");
      return null;
    }
  }, [chantierId, cle, versionId]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    api.get(`/chantiers/${chantierId}/marches`).then((d) => setLots(Array.isArray(d) ? d : [])).catch(() => setLots([]));
  }, [chantierId]);

  // Taille du cadre (rotation de l'iPad, redimensionnement de la fenêtre)
  useEffect(() => {
    const el = cadre.current;
    if (!el) return undefined;
    const maj = () => setTaille({ w: el.clientWidth, h: el.clientHeight });
    maj();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(maj) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', maj);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', maj); };
  }, [plan]);

  // Image de la page (rendu serveur), plus précise dès qu'on zoome (sans revenir en arrière)
  const [hauteDef, setHauteDef] = useState(false);
  useEffect(() => {
    if (!hauteDef && vue.s * PW * (window.devicePixelRatio || 1) > 1700) setHauteDef(true);
  }, [vue.s, hauteDef]);
  const largeurVoulue = hauteDef ? 2400 : 1600;
  useEffect(() => {
    if (!plan) return undefined;
    let vivant = true;
    let objet = null;
    const doc = versionId ? `&document_id=${enc(versionId)}` : '';
    api.getBlob(`/chantiers/${chantierId}/plans-annotes/rendu?cle=${enc(cle)}&page=${page}&largeur=${largeurVoulue}${doc}`)
      .then((b) => {
        objet = URL.createObjectURL(b);
        const img = new Image();
        img.onload = () => {
          if (!vivant) return;
          setImage((prec) => {
            if (prec?.url) URL.revokeObjectURL(prec.url);
            return { url: objet, w: img.naturalWidth, h: img.naturalHeight, largeur: largeurVoulue, cle: `${page}|${versionId}` };
          });
        };
        img.src = objet;
      })
      .catch((e) => { if (vivant) setErreur(e.message || "Le plan n'a pas pu être affiché"); });
    return () => { vivant = false; };
  }, [plan, page, versionId, largeurVoulue, chantierId, cle]);

  const ajuster = useCallback(() => {
    if (!image || !taille.w) return;
    const ph = (PW * image.h) / image.w;
    const s = Math.min(taille.w / PW, taille.h / ph) * 0.96;
    setVue({ s, tx: (taille.w - PW * s) / 2, ty: (taille.h - ph * s) / 2 });
  }, [image, taille.w, taille.h]);
  // Recadrer au changement de page / d'indice / de taille (pas au passage en haute définition)
  const cleImage = image?.cle;
  const ajusterRef = useRef(ajuster);
  ajusterRef.current = ajuster;
  useEffect(() => { ajusterRef.current(); }, [cleImage, taille.w, taille.h]);

  // Ouvrir sur une épingle (depuis la fiche d'une action)
  const focusFait = useRef(false);
  useEffect(() => {
    if (!focusPin || focusFait.current || !plan || !image || !taille.w) return;
    const p = (plan.epingles || []).find((e) => e.id === focusPin);
    if (!p) return;
    focusFait.current = true;
    if (p.page !== page) { setPage(Math.min(p.page, plan.pages)); focusFait.current = false; return; }
    const ph = (PW * image.h) / image.w;
    const s = Math.min(taille.w / PW, taille.h / ph) * 2.5;
    setVue({ s, tx: taille.w / 2 - p.x * PW * s, ty: taille.h / 2 - p.y * ph * s });
    setFiche(p);
  }, [focusPin, plan, image, taille.w, taille.h, page]);

  const bornes = useCallback((s) => {
    if (!image || !taille.w) return s;
    const ph = (PW * image.h) / image.w;
    const fit = Math.min(taille.w / PW, taille.h / ph);
    return Math.max(fit * 0.5, Math.min(fit * 14, s));
  }, [image, taille.w, taille.h]);

  const zoomer = useCallback((facteur, cx, cy) => {
    setVue((v) => {
      const s = bornes(v.s * facteur);
      const k = s / v.s;
      const x = cx ?? taille.w / 2;
      const y = cy ?? taille.h / 2;
      return { s, tx: x - (x - v.tx) * k, ty: y - (y - v.ty) * k };
    });
  }, [bornes, taille.w, taille.h]);

  const versPlan = (sx, sy) => {
    const v = vueRef.current;
    return { x: (sx - v.tx) / (PW * v.s), y: (sy - v.ty) / (PH * v.s) };
  };
  const local = (e) => {
    const r = cadre.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const surTap = (sx, sy) => {
    const p = versPlan(sx, sy);
    if (p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1) return;
    const pt = { page, x: Math.round(p.x * 100000) / 100000, y: Math.round(p.y * 100000) / 100000 };
    if (mode === 'placer') { setPoint(pt); return; }
    if (mode === 'annoter' && outil === 'texte') { setTexteEnAttente(pt); return; }
    if (mode === 'consulter' && plan?.peut_annoter) { setPoint(pt); setChoix(true); }
  };

  const onPointerDown = (e) => {
    if (!image) return;
    cadre.current.setPointerCapture?.(e.pointerId);
    const l = local(e);
    pointeurs.current.set(e.pointerId, l);
    if (pointeurs.current.size === 2) {
      const [a, b] = [...pointeurs.current.values()];
      geste.current = { type: 'pince', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, v0: vueRef.current };
      setTrace(null);
      return;
    }
    if (mode === 'annoter' && outil !== 'texte' && (e.pointerType !== 'mouse' || e.button === 0)) {
      const p = versPlan(l.x, l.y);
      geste.current = { type: 'dessin' };
      setTrace([[p.x, p.y]]);
      return;
    }
    geste.current = { type: 'tap', d: l, v0: vueRef.current, bouge: false };
  };

  const onPointerMove = (e) => {
    if (!pointeurs.current.has(e.pointerId)) return;
    const l = local(e);
    pointeurs.current.set(e.pointerId, l);
    const g = geste.current;
    if (!g) return;
    if (g.type === 'pince' && pointeurs.current.size >= 2) {
      const [a, b] = [...pointeurs.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = bornes(g.v0.s * (d / g.d0));
      const px = (g.m0.x - g.v0.tx) / g.v0.s;
      const py = (g.m0.y - g.v0.ty) / g.v0.s;
      setVue({ s, tx: m.x - px * s, ty: m.y - py * s });
    } else if (g.type === 'dessin') {
      const p = versPlan(l.x, l.y);
      setTrace((t) => {
        if (!t) return t;
        const [lx, ly] = t[t.length - 1];
        if (Math.hypot((p.x - lx) * PW, (p.y - ly) * PH) < 4 / vueRef.current.s) return t;
        return t.length >= 400 ? t : [...t, [Math.min(1, Math.max(0, p.x)), Math.min(1, Math.max(0, p.y))]];
      });
    } else if (g.type === 'tap') {
      const dx = l.x - g.d.x;
      const dy = l.y - g.d.y;
      if (g.bouge || Math.hypot(dx, dy) > 8) {
        g.bouge = true;
        setVue({ s: g.v0.s, tx: g.v0.tx + dx, ty: g.v0.ty + dy });
      }
    }
  };

  const terminerTrace = async (t) => {
    if (!t || t.length < 2) return;
    let points = t;
    if (outil === 'fleche' || outil === 'zone') points = [t[0], t[t.length - 1]];
    const [a, b] = [points[0], points[points.length - 1]];
    if (Math.hypot((a[0] - b[0]) * PW, (a[1] - b[1]) * PH) < 6 && outil !== 'trait') return;
    await envoyerAnnotation({ forme: outil, points });
  };

  const onPointerUp = (e) => {
    const g = geste.current;
    const l = pointeurs.current.get(e.pointerId);
    pointeurs.current.delete(e.pointerId);
    if (!g) return;
    if (g.type === 'pince') {
      if (pointeurs.current.size === 0) geste.current = null;
      else {
        const [reste] = [...pointeurs.current.values()];
        geste.current = { type: 'tap', d: reste, v0: vueRef.current, bouge: true };
      }
      return;
    }
    geste.current = null;
    if (g.type === 'dessin') {
      const t = trace;
      setTrace(null);
      terminerTrace(t);
      return;
    }
    if (g.type === 'tap' && !g.bouge && l && e.type !== 'pointercancel') surTap(l.x, l.y);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const l = local(e);
    zoomer(Math.exp(-e.deltaY * 0.0015), l.x, l.y);
  };
  useEffect(() => {
    const el = cadre.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const envoyerAnnotation = async (corps) => {
    try {
      await api.post(`/chantiers/${chantierId}/plans-annotes/annotations`, { plan_key: cle, page, couleur, ...corps });
      charger();
    } catch (e2) {
      toast.error(e2.message || "L'annotation n'a pas été enregistrée");
    }
  };

  const supprimerAnnotation = async (a) => {
    try {
      await api.delete(`/chantiers/${chantierId}/plans-annotes/annotations/${a.id}`);
      charger();
    } catch (e) { toast.error(e.message || 'Suppression impossible'); }
  };

  const placerIci = async () => {
    if (!point || !placement) return;
    try {
      await api.put(`/chantiers/${chantierId}/elements/${placement.id}/epingle`, {
        plan_key: cle, document_id: plan?.version?.document_id || null, page: point.page, x: point.x, y: point.y,
      });
      toast.success("L'action est placée sur le plan");
      setPoint(null);
      if (onPlace) onPlace();
      else { setMode('consulter'); charger(); }
    } catch (e) { toast.error(e.message || 'Placement impossible'); }
  };

  const retirerEpingle = async (pin) => {
    try {
      await api.delete(`/chantiers/${chantierId}/elements/${pin.id}/epingle`);
      setFiche(null);
      charger();
    } catch (e) { toast.error(e.message || 'Impossible de retirer'); }
  };

  const epingles = useMemo(() => plan?.epingles || [], [plan]);
  const entreprises = useMemo(() => [...new Set(epingles.map((p) => p.entreprise).filter(Boolean))].sort(), [epingles]);
  const lotsPins = useMemo(() => [...new Set(epingles.map((p) => p.lot).filter(Boolean))].sort(), [epingles]);
  const visibles = epingles.filter((p) => (!filtres.statuts.length || filtres.statuts.includes(p.statut))
    && (!filtres.entreprise || p.entreprise === filtres.entreprise)
    && (!filtres.lot || p.lot === filtres.lot));
  const nbPages = plan?.pages || 1;
  const surPage = (p) => Math.min(p.page || 1, nbPages) === page;

  const exporter = async () => {
    setExport(true);
    try {
      const q = [`cle=${enc(cle)}`];
      if (filtres.statuts.length) q.push(`statut=${enc(filtres.statuts.join(','))}`);
      if (filtres.entreprise) q.push(`entreprise=${enc(filtres.entreprise)}`);
      if (filtres.lot) q.push(`lot=${enc(filtres.lot)}`);
      const b = await api.getBlob(`/chantiers/${chantierId}/plans-annotes/export.pdf?${q.join('&')}`);
      const url = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Plan annoté - ${plan?.titre || 'plan'}${plan?.version?.indice ? ` ind ${plan.version.indice}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.message || "L'export a échoué"); }
    finally { setExport(false); }
  };

  const ouvrirOriginal = async () => {
    const v = plan?.version;
    if (!v?.fichier_url) return;
    const fenetre = window.open('', '_blank');
    try {
      const b = await api.getBlob(String(v.fichier_url).replace(/^\/api/, ''));
      const url = URL.createObjectURL(b);
      if (fenetre) fenetre.location.href = url; else window.location.href = url;
    } catch (e) { if (fenetre) fenetre.close(); toast.error(e.message || 'Fichier indisponible'); }
  };

  const basculerStatut = (s) => setFiltres((f) => ({ ...f, statuts: f.statuts.includes(s) ? f.statuts.filter((x) => x !== s) : [...f.statuts, s] }));
  const nbFiltres = filtres.statuts.length + (filtres.entreprise ? 1 : 0) + (filtres.lot ? 1 : 0);
  const pos = (x, y) => ({ left: vue.tx + x * PW * vue.s, top: vue.ty + y * PH * vue.s });
  const anciens = plan?.anciens || [];

  return auCorps(
    <div role="dialog" aria-label={plan?.titre || 'Plan'} data-testid="plan-annote"
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: C.fond, display: 'flex', flexDirection: 'column', color: 'white' }}>
      {/* Barre du haut : plan, indice, export */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', paddingTop: 'calc(6px + env(safe-area-inset-top, 0px))', background: C.barre }}>
        <button onClick={onFermer} aria-label="Fermer" style={btn(false, { border: 'none', fontSize: 22 })} data-testid="plan-annote-fermer">×</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plan?.titre || 'Plan'}</p>
          {plan && (
            <select value={versionId || ''} onChange={(e) => { setVersionId(e.target.value || null); setPage(1); }}
              aria-label="Indice" data-testid="plan-annote-indice"
              style={{ background: 'transparent', color: plan.version?.remplace ? '#E8A87C' : '#B8D8C4', border: 'none', fontSize: 12, fontFamily: 'inherit', padding: 0, maxWidth: '100%' }}>
              <option value="">{libelleIndice(plan.en_vigueur)} · en vigueur</option>
              {anciens.map((a) => <option key={a.document_id} value={a.document_id}>{libelleIndice(a)} · remplacé</option>)}
            </select>
          )}
        </div>
        <button onClick={ouvrirOriginal} style={btn(false)} title="Fichier d'origine" data-testid="plan-annote-original">PDF</button>
        <button onClick={exporter} disabled={export_ || !plan} style={btn(false)} data-testid="plan-annote-export">
          {export_ ? 'Export…' : 'Exporter'}
        </button>
      </div>

      {plan?.version?.remplace && (
        <div style={{ background: '#8B5A2B', color: 'white', fontSize: 12, padding: '6px 12px' }} data-testid="plan-annote-remplace">
          Indice remplacé : consultez l'indice en vigueur pour travailler. Les épingles suivent le plan d'un indice à l'autre.
        </div>
      )}

      {/* Barre d'outils : mode, filtres, zoom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', background: '#252525', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {placement ? (
          <span style={{ fontSize: 13, padding: '0 6px' }} data-testid="plan-annote-consigne-placer">
            Touchez l'endroit où placer : <strong>{placement.titre}</strong>
          </span>
        ) : (
          <>
            {plan?.peut_annoter && (
              <button onClick={() => { setMode(mode === 'annoter' ? 'consulter' : 'annoter'); setPoint(null); }}
                style={btn(mode === 'annoter')} data-testid="plan-annote-mode-annoter">
                {mode === 'annoter' ? 'Terminer' : 'Annoter'}
              </button>
            )}
            {mode === 'annoter' ? (
              <>
                {OUTILS.map((o) => (
                  <button key={o.cle} onClick={() => setOutil(o.cle)} style={btn(outil === o.cle)} data-testid={`plan-annote-outil-${o.cle}`}>{o.libelle}</button>
                ))}
                {COULEURS_TRAIT.map((c) => (
                  <button key={c} onClick={() => setCouleur(c)} aria-label={`Couleur ${c}`}
                    style={{ ...btn(false, { minWidth: 36, padding: 0 }), background: c, outline: couleur === c ? '2px solid white' : 'none' }} />
                ))}
              </>
            ) : (
              <button onClick={() => setPanneauFiltres((x) => !x)} style={btn(panneauFiltres || nbFiltres > 0)} data-testid="plan-annote-filtres">
                Filtres{nbFiltres ? ` (${nbFiltres})` : ''} · {visibles.length} action{visibles.length > 1 ? 's' : ''}
              </button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        {nbPages > 1 && (
          <>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={btn(false)} aria-label="Page précédente">‹</button>
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Page {page}/{nbPages}</span>
            <button onClick={() => setPage((p) => Math.min(nbPages, p + 1))} disabled={page >= nbPages} style={btn(false)} aria-label="Page suivante">›</button>
          </>
        )}
        <button onClick={() => zoomer(1 / 1.5)} style={btn(false)} aria-label="Dézoomer">−</button>
        <button onClick={() => zoomer(1.5)} style={btn(false)} aria-label="Zoomer">+</button>
        <button onClick={ajuster} style={btn(false)} data-testid="plan-annote-ajuster">Ajuster</button>
      </div>

      {panneauFiltres && mode !== 'annoter' && !placement && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px', background: '#303030' }} data-testid="plan-annote-panneau-filtres">
          {(plan?.statuts || []).map((s) => (
            <button key={s.cle} onClick={() => basculerStatut(s.cle)} aria-pressed={filtres.statuts.includes(s.cle)}
              style={btn(false, { borderColor: s.couleur, background: filtres.statuts.includes(s.cle) ? s.couleur : 'transparent' })}
              data-testid={`plan-annote-filtre-${s.cle}`}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: s.couleur, display: 'inline-block' }} />
              {s.libelle}
            </button>
          ))}
          {entreprises.length > 0 && (
            <select value={filtres.entreprise} onChange={(e) => setFiltres((f) => ({ ...f, entreprise: e.target.value }))}
              style={{ ...champ, width: 'auto', minHeight: 44, padding: '0 8px', fontSize: 13 }} aria-label="Entreprise" data-testid="plan-annote-filtre-entreprise">
              <option value="">Toutes les entreprises</option>
              {entreprises.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          {lotsPins.length > 0 && (
            <select value={filtres.lot} onChange={(e) => setFiltres((f) => ({ ...f, lot: e.target.value }))}
              style={{ ...champ, width: 'auto', minHeight: 44, padding: '0 8px', fontSize: 13 }} aria-label="Lot" data-testid="plan-annote-filtre-lot">
              <option value="">Tous les lots</option>
              {lotsPins.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          {nbFiltres > 0 && (
            <button onClick={() => setFiltres({ statuts: [], entreprise: '', lot: '' })} style={btn(false)}>Effacer</button>
          )}
        </div>
      )}

      {/* Plan : zoom au doigt, toucher pour créer */}
      <div ref={cadre}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        onDoubleClick={(e) => { const l = local(e); zoomer(2, l.x, l.y); }}
        style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', cursor: mode === 'annoter' ? 'crosshair' : 'grab', background: C.fond }}
        data-testid="plan-annote-cadre">
        {erreur && <p style={{ padding: 24, textAlign: 'center' }} data-testid="plan-annote-erreur-plan">{erreur}</p>}
        {!erreur && !image && <p style={{ padding: 24, textAlign: 'center', color: '#BBB' }}>Chargement du plan…</p>}
        {image && (
          <div style={{ position: 'absolute', left: 0, top: 0, width: PW, height: PH, transformOrigin: '0 0', transform: `translate(${vue.tx}px, ${vue.ty}px) scale(${vue.s})`, background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
            <img src={image.url} alt={plan?.titre || ''} draggable={false} style={{ width: PW, height: PH, display: 'block', pointerEvents: 'none' }} />
            <svg width={PW} height={PH} viewBox={`0 0 ${PW} ${PH}`} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <defs>
                {COULEURS_TRAIT.map((c) => (
                  <marker key={c} id={`pa-fleche-${c.slice(1)}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={c} />
                  </marker>
                ))}
              </defs>
              {(plan?.annotations || []).filter((a) => (a.page || 1) === page).map((a) => <Forme key={a.id} a={a} ph={PH} />)}
              {trace && <Forme a={{ forme: outil === 'trait' ? 'trait' : outil, points: outil === 'trait' ? trace : [trace[0], trace[trace.length - 1]], couleur }} ph={PH} />}
            </svg>
          </div>
        )}
        {/* Pastilles (taille constante quel que soit le zoom) */}
        {image && mode !== 'annoter' && visibles.filter(surPage).map((p) => {
          const { left, top } = pos(p.x, p.y);
          return (
            <button key={p.id} onPointerDown={(e) => e.stopPropagation()} onClick={() => setFiche(p)}
              aria-label={`Action ${p.numero} : ${p.titre} (${p.statut_libelle})`}
              style={{ position: 'absolute', left: left - 22, top: top - 22, width: 44, height: 44, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              data-testid={`plan-annote-epingle-${p.id}`}>
              <span style={{ width: 28, height: 28, borderRadius: 14, background: p.couleur, color: 'white', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: p.proposition ? '2px dashed white' : '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                {p.numero}
              </span>
            </button>
          );
        })}
        {/* En mode annotation : effacer une annotation */}
        {image && mode === 'annoter' && (plan?.annotations || []).filter((a) => (a.page || 1) === page).map((a) => {
          const [x, y] = a.points[0] || [0, 0];
          const { left, top } = pos(x, y);
          return (
            <button key={a.id} onPointerDown={(e) => e.stopPropagation()} onClick={() => supprimerAnnotation(a)}
              aria-label="Effacer cette annotation"
              style={{ position: 'absolute', left: left - 30, top: top - 30, width: 26, height: 26, minWidth: 26, minHeight: 26, borderRadius: 13, border: '2px solid white', background: '#333', color: 'white', fontSize: 15, lineHeight: '20px', padding: 0, cursor: 'pointer' }}
              data-testid={`plan-annote-effacer-${a.id}`}>×</button>
          );
        })}
        {/* Point touché */}
        {point && image && (
          <span style={{ position: 'absolute', ...(() => { const q = pos(point.x, point.y); return { left: q.left - 12, top: q.top - 12 }; })(), width: 24, height: 24, borderRadius: 12, border: '3px solid white', background: C.accent, boxShadow: '0 0 0 4px rgba(196,119,59,0.35)', pointerEvents: 'none' }}
            data-testid="plan-annote-point" />
        )}
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '6px 12px', paddingBottom: 'calc(6px + env(safe-area-inset-bottom, 0px))', background: C.barre, fontSize: 12, flexWrap: 'wrap' }}>
        {(plan?.statuts || []).map((s) => (
          <span key={s.cle} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: s.couleur }} />{s.libelle}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        {placement && point && (
          <button onClick={placerIci} style={btn(true)} data-testid="plan-annote-placer-ici">Placer ici</button>
        )}
        {!placement && plan?.peut_annoter && mode === 'consulter' && (
          <span style={{ color: '#BBB' }}>Touchez le plan pour ajouter une réserve, une action ou une photo</span>
        )}
      </div>

      {choix && point && (
        <Feuille titre="Ajouter à cet endroit" onFermer={() => { setChoix(false); setPoint(null); }} testid="plan-annote-choix">
          <div style={{ display: 'flex', gap: 10 }}>
            {TYPES_CREATION.map((t) => (
              <button key={t.cle} onClick={() => { setChoix(false); setFormType(t.cle); }}
                style={{ flex: 1, minHeight: 64, border: `1px solid ${C.bord}`, background: C.clair, fontSize: 16, fontWeight: 700, color: C.texte, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
                data-testid={`plan-annote-choix-${t.cle}`}>
                {t.libelle}
              </button>
            ))}
          </div>
        </Feuille>
      )}
      {formType && point && plan && (
        <FormulaireAction key={formType} plan={plan} point={point} lots={lots} chantierId={chantierId}
          onAnnuler={() => { setFormType(null); setPoint(null); }}
          onCree={() => { setFormType(null); setPoint(null); charger(); }}
          typeInitial={formType} />
      )}
      {texteEnAttente && (
        <SaisieTexte onAnnuler={() => setTexteEnAttente(null)}
          onValider={(t) => { const p = texteEnAttente; setTexteEnAttente(null); envoyerAnnotation({ forme: 'texte', points: [[p.x, p.y]], texte: t }); }} />
      )}
      {fiche && (
        <FicheEpingle pin={fiche} onFermer={() => setFiche(null)} peutAnnoter={!!plan?.peut_annoter}
          onRetirer={retirerEpingle}
          onOuvrirAction={onOuvrirAction ? (id) => { setFiche(null); onOuvrirAction(id); } : null} />
      )}
    </div>,
  );
}

function Forme({ a, ph }) {
  const pts = (a.points || []).map(([x, y]) => [x * PW, y * ph]);
  const c = a.couleur || '#C0392B';
  const trait = { stroke: c, strokeWidth: 3, fill: 'none', vectorEffect: 'non-scaling-stroke', strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (!pts.length) return null;
  if (a.forme === 'trait') return <polyline points={pts.map((p) => p.join(',')).join(' ')} style={trait} />;
  if (a.forme === 'fleche' && pts.length >= 2) {
    const [p0, p1] = [pts[0], pts[pts.length - 1]];
    return <line x1={p0[0]} y1={p0[1]} x2={p1[0]} y2={p1[1]} style={trait} markerEnd={`url(#pa-fleche-${c.slice(1)})`} />;
  }
  if (a.forme === 'zone' && pts.length >= 2) {
    const [p0, p1] = [pts[0], pts[pts.length - 1]];
    return <rect x={Math.min(p0[0], p1[0])} y={Math.min(p0[1], p1[1])} width={Math.abs(p1[0] - p0[0])} height={Math.abs(p1[1] - p0[1])} style={{ ...trait, fill: `${c}22` }} />;
  }
  if (a.forme === 'texte') {
    return (
      <text x={pts[0][0]} y={pts[0][1]} fill={c} fontSize={PW / 55} fontWeight="700" fontFamily="Helvetica, Arial, sans-serif"
        style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: 4 }}>{a.texte}</text>
    );
  }
  return null;
}
